import React, { useCallback } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/pro-light-svg-icons/faLock';
import { TypedMemo } from '../../../common/types';
import { LockChip } from '../../components/LockedControl/LockedControl';
import { getPlanCapabilities, pickPlanForFeature, SMLUpgradeFeature } from '../../config/music24PlanCapabilities';
import { useMusic24Capabilities } from '../../hooks/useMusic24Capabilities';
import { useUpgradePrompt } from '../../hooks/useUpgradePrompt';
import { SerializedSocialChannel } from '../types';
import { xMessages } from '../xMessages';
import { xChannelNeedsPro, xConnectLocked } from '../xPostsLimit';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Promote pillar; each part uses its own slice of it */
import shared from '../Promote.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ChannelXPlanNoteProps {
  /** The X channel. */
  channel: SerializedSocialChannel;
  disabled?: boolean;
}

/**
 * The plan note of the X card (design x-connector a1, a3, b2):
 * - a login on a plan without X: "Posting to X needs Pro" with the lock chip, which opens the X
 *   prompt, and what stays;
 * - a handle only: the artist posts to X by hand;
 * - no login and no handle on a plan without X: what Pro adds.
 */
const ChannelXPlanNoteComponent: React.FC<ChannelXPlanNoteProps> = ({ channel, disabled = false }) => {
  const intl = useIntl();
  const capabilities = useMusic24Capabilities();
  const openUpgradePrompt = useUpgradePrompt();
  const openXPrompt = useCallback(() => openUpgradePrompt(SMLUpgradeFeature.x), [openUpgradePrompt]);

  const hasHandle = channel.handle !== null && channel.handle !== '';

  if (xChannelNeedsPro(channel, capabilities.canUseX)) {
    return (
      <>
        <button
          type="button"
          className={clsx(shared.stateLine, shared.stateLineButton, shared.stateLineInfo)}
          disabled={disabled}
          onClick={openXPrompt}
          data-testid="x-needs-pro"
        >
          <FontAwesomeIcon icon={faLock} className={shared.stateLineIcon} />
          <span className={shared.stateLineText}>{intl.formatMessage(xMessages.needsPro)}</span>
          <LockChip feature={SMLUpgradeFeature.x} />
        </button>
        <p className={shared.stateLineDetail}>{withBrandWord(intl.formatMessage(xMessages.needsProBody))}</p>
      </>
    );
  }

  if (hasHandle && channel.connection === null && channel.status !== 'connected') {
    return (
      <p className={shared.stateLineDetail} data-testid="x-manual-note">
        {withBrandWord(intl.formatMessage(xMessages.manualNote))}
      </p>
    );
  }

  if (xConnectLocked(channel, capabilities.canUseX) && channel.autoPublishAvailable && channel.connection === null) {
    // The posts of the plan that the lock offers (Pro): never a typed number.
    const posts = getPlanCapabilities(pickPlanForFeature(SMLUpgradeFeature.x, capabilities)).xPostsPerMonth;
    return (
      <p className={shared.stateLineDetail} data-testid="x-locked-note">
        {intl.formatMessage(xMessages.lockedNote, { posts })}
      </p>
    );
  }

  return null;
};

export const ChannelXPlanNote = TypedMemo(ChannelXPlanNoteComponent);
